import { Component, OnDestroy, OnInit } from '@angular/core';
import { AdminContactDepartment } from '@app/model/contact.model';
import { ContactManagementDataService } from '@app/services/contact-management-data.service';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, take, takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material';
import { ContactDepartmentFormComponent } from '../contact-department-form/contact-department-form.component';
import { LayoutUtilsService } from '@app/core/_base/crud';
import { ToastrService } from 'ngx-toastr';
import { environment } from '@app/../environments/environment';

@Component({
  selector: 'app-contact-department-management',
  templateUrl: './contact-department-management.component.html',
  styleUrls: ['./contact-department-management.component.scss']
})
export class ContactDepartmentManagementComponent implements OnInit, OnDestroy {
  departments: AdminContactDepartment[] = [];

  searchText = '';
  statusFilter = 'all';

  pageIndex = 1;
  pageSize = 10;
  totalItems = 0;
  pageSizeOptions: number[] = [5, 10, 20, 100];

  isLoading = false;
  errorMessage = '';
  deletingDepartmentId = '';

  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();
  private departmentsSubscription = new Subscription();

  constructor(
    private contactManagementDataService: ContactManagementDataService,
    private dialog: MatDialog,
    private layoutUtilsService: LayoutUtilsService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    // Debounce search 500ms then reload from the server.
    this.searchSubject
      .pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.pageIndex = 1;
        this.loadDepartments();
      });

    this.loadDepartments();
  }

  ngOnDestroy(): void {
    this.departmentsSubscription.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
    this.searchSubject.complete();
  }

  getDepartmentImageUrl(imagePath: string | null): string {
    if (!imagePath) {
      return '';
    }
    if (imagePath.indexOf('http') === 0) {
      return imagePath;
    }
    return environment.backendhost + imagePath;
  }

  /** Build the Sieve filters string from search/status (AND via comma). */
  private buildFilters(): string {
    const conditions: string[] = [];
    const keyword = (this.searchText || '').trim();
    if (keyword) {
      // Per guide: name|code|description@=keyword
      conditions.push(`name|code|description@=${keyword}`);
    }
    if (this.statusFilter !== 'all') {
      // Departments only support the snake_case `is_active` (see guide section 3.2).
      conditions.push(`is_active==${this.statusFilter === 'active' ? 'true' : 'false'}`);
    }
    return conditions.join(',');
  }

  loadDepartments(): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.departmentsSubscription.unsubscribe();
    this.departmentsSubscription = this.contactManagementDataService
      .getDepartmentsPaged({
        filters: this.buildFilters(),
        sorts: 'sort_order',
        page: this.pageIndex,
        pageSize: this.pageSize
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (result) => {
          this.departments = (result && result.collection) || [];
          this.totalItems = (result && result.total) || 0;
          this.isLoading = false;
        },
        (error) => {
          console.error('Load contact departments failed:', error);
          this.departments = [];
          this.totalItems = 0;
          this.isLoading = false;
          this.errorMessage = 'Không thể tải danh sách phòng ban';
        }
      );
  }

  onSearchChange(value: string): void {
    this.searchText = value;
    this.searchSubject.next(value);
  }

  onStatusFilterChange(): void {
    this.pageIndex = 1;
    this.loadDepartments();
  }

  clearFilters(): void {
    this.searchText = '';
    this.statusFilter = 'all';
    this.pageIndex = 1;
    this.loadDepartments();
  }

  onPageChange(pageNumber: number): void {
    if (typeof pageNumber !== 'number') {
      return;
    }
    this.pageIndex = pageNumber;
    this.loadDepartments();
    window.scrollTo(0, 0);
  }

  onPageSizeChange(newSize: number): void {
    this.pageSize = newSize;
    this.pageIndex = 1;
    this.loadDepartments();
  }

  openCreateDepartment(): void {
    const dialogRef = this.dialog.open(ContactDepartmentFormComponent, {
      width: '960px',
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: '90vh',
      autoFocus: false,
      disableClose: true,
      panelClass: 'contact-department-form-dialog',
      data: { mode: 'create' }
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result && result.created) {
          this.loadDepartments();
        }
      });
  }

  openEditDepartment(department: AdminContactDepartment): void {
    const dialogRef = this.dialog.open(ContactDepartmentFormComponent, {
      width: '960px',
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: '90vh',
      autoFocus: false,
      disableClose: true,
      panelClass: 'contact-department-form-dialog',
      data: { mode: 'edit', department }
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result && result.updated) {
          this.loadDepartments();
        }
      });
  }

  confirmDeleteDepartment(department: AdminContactDepartment): void {
    const title = 'Xóa phòng ban';
    const description = 'Bạn có chắc chắn muốn xóa phòng ban "' + (department.name || department.code) + '" không?';
    const waitDescription = 'Đang xóa phòng ban...';

    const dialogRef = this.layoutUtilsService.deleteElement(title, description, waitDescription);

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }
        this.deleteDepartment(department);
      });
  }

  private deleteDepartment(department: AdminContactDepartment): void {
    this.deletingDepartmentId = department.id;

    this.contactManagementDataService
      .deleteDepartment(department.id)
      .pipe(
        take(1),
        finalize(() => {
          this.deletingDepartmentId = '';
        })
      )
      .subscribe(
        (response) => {
          if (!response || !response.success) {
            this.toastr.error(
              response && response.message ? response.message : 'Không thể xóa phòng ban'
            );
            return;
          }
          this.toastr.success('Xóa phòng ban thành công');
          this.loadDepartments();
        },
        (error) => {
          const errorResponse = error && error.error ? error.error : null;
          let message = 'Không thể xóa phòng ban';
          if (errorResponse && errorResponse.message) {
            message = errorResponse.message;
          } else if (errorResponse && errorResponse.title) {
            message = errorResponse.title;
          } else if (error.status === 400 || error.status === 409) {
            message = 'Phòng ban còn nhân sự nên không thể xóa';
          }
          this.toastr.error(message);
        }
      );
  }
}
